import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../utilities/axiosInstance";

const resourceState = () => ({
  data: [],
  itemsCount: 0,
  pages: 0,
  isLoading: false,
  error: null,
});

const initialState = {
  permissions: resourceState(),
  groups: resourceState(),
  roles: resourceState(),
};

const errorOf = (e) => e.response?.data ?? { message: e.message };

const makeThunks = (resource, { paginated }) => ({
  fetch: createAsyncThunk(`roleManagement/${resource}/fetch`, async (args = {}, api) => {
    try {
      const { page = 1, size = 10 } = args;
      const url = paginated ? `/${resource}?page=${page}&size=${size}` : `/${resource}`;
      const { data } = await axiosInstance.get(url);
      return data;
    } catch (e) {
      return api.rejectWithValue(errorOf(e));
    }
  }),
  create: createAsyncThunk(`roleManagement/${resource}/create`, async (values, api) => {
    try {
      const { data } = await axiosInstance.post(`/${resource}`, values);
      return data;
    } catch (e) {
      return api.rejectWithValue(errorOf(e));
    }
  }),
  update: createAsyncThunk(`roleManagement/${resource}/update`, async ({ _id, values }, api) => {
    try {
      const { data } = await axiosInstance.put(`/${resource}/${_id}`, values);
      return data;
    } catch (e) {
      return api.rejectWithValue(errorOf(e));
    }
  }),
  remove: createAsyncThunk(`roleManagement/${resource}/remove`, async ({ _id }, api) => {
    try {
      const { data } = await axiosInstance.delete(`/${resource}/${_id}`);
      return data;
    } catch (e) {
      return api.rejectWithValue(errorOf(e));
    }
  }),
});

const permissionsThunks = makeThunks("permissions", { paginated: false });
const groupsThunks = makeThunks("groups", { paginated: true });
const rolesThunks = makeThunks("roles", { paginated: true });

export const getPermissions = permissionsThunks.fetch;

export const getGroups = groupsThunks.fetch;
export const createGroup = groupsThunks.create;
export const updateGroup = groupsThunks.update;
export const deleteGroup = groupsThunks.remove;

export const getRoles = rolesThunks.fetch;
export const createRole = rolesThunks.create;
export const updateRole = rolesThunks.update;
export const deleteRole = rolesThunks.remove;

const roleManagementSlice = createSlice({
  name: "roleManagement",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    const register = (resource, thunks, paginated) => {
      // list
      builder
        .addCase(thunks.fetch.pending, (state) => {
          state[resource].isLoading = true;
          state[resource].error = null;
        })
        .addCase(thunks.fetch.fulfilled, (state, { payload }) => {
          const s = state[resource];
          s.isLoading = false;
          if (paginated) {
            s.data = payload.data?.data || [];
            s.itemsCount = payload.data?.itemsCount || 0;
            s.pages = payload.data?.pages || 0;
          } else {
            s.data = payload.data || [];
            s.itemsCount = s.data.length;
            s.pages = 1;
          }
        })
        .addCase(thunks.fetch.rejected, (state, { payload }) => {
          state[resource].isLoading = false;
          state[resource].error = payload?.message;
        });

      // mutations only toggle loading/error; the page refetches the list afterwards
      ["create", "update", "remove"].forEach((op) => {
        if (!thunks[op]) return;
        builder
          .addCase(thunks[op].pending, (state) => {
            state[resource].isLoading = true;
            state[resource].error = null;
          })
          .addCase(thunks[op].fulfilled, (state) => {
            state[resource].isLoading = false;
          })
          .addCase(thunks[op].rejected, (state, { payload }) => {
            state[resource].isLoading = false;
            state[resource].error = payload?.message;
          });
      });
    };

    register("permissions", { fetch: permissionsThunks.fetch }, false);
    register("groups", groupsThunks, true);
    register("roles", rolesThunks, true);
  },
});

export default roleManagementSlice.reducer;